import { useCallback, useEffect, useMemo, useState } from "react";
import { Pagination } from "antd";
import dayjs from "dayjs";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useCurrentUser } from "../host/useCurrentUser";
import { useTeamMembers } from "./hooks/useTeamMembers";
import { useAccount } from "./hooks/useAccount";
import { useInvitations } from "./hooks/useInvitations";
import { useUpdateMemberRole } from "./hooks/useUpdateMemberRole";
import { useUpdateMemberLicense } from "./hooks/useUpdateMemberLicense";
import { useRemoveMember } from "./hooks/useRemoveMember";
import { useLeaveTeam } from "./hooks/useLeaveTeam";
import { useWithdrawInvitation } from "./hooks/useWithdrawInvitation";
import { useCreateCustomerPortal } from "./hooks/useCreateCustomerPortal";
import { useGoToBulkLicensePage } from "./hooks/useGoToBulkLicensePage";
import { useHostConfirm } from "../host/useHostConfirm";
import { useHostToast } from "../host/useHostToast";
import { useHostRequestAdditionalLicense } from "../host/useHostRequestAdditionalLicense";
import { useHostReportSelection } from "../host/useHostReportSelection";
import { useHostBulkActionListener } from "../host/useHostBulkActionListener";
import { useTrack } from "../host/useTrack";
import { getHost } from "../host/getHost";
import { isHostFeatureEnabled, SCIM_MANUAL_MANAGEMENT_FLAG } from "../host/hostFeatureFlags";
import { grantLicenseBulk, revokeLicenseBulk } from "./api/licenses";
import {
  buildInvitationsCsv,
  buildMembersCsv,
  downloadCsv,
  toCsv,
} from "./exportCsv";
import {
  isAssignableMember,
  isRevocableMember,
  isRoleActionableByViewer,
} from "./memberPlanRules";

// Mirrors `eventCategories.MY_TEAM` from the original presenter so the
// names that land in Mixpanel/GA4 are byte-identical to pre-migration.
const MY_TEAM = "My Team";
import { ActionButtons } from "./components/ActionButtons";
import { ExportButton } from "./components/ExportButton";
import { InvitationsTable } from "./components/InvitationsTable";
import { LicenseShortageAlert } from "./components/LicenseShortageAlert";
import { LicenseUsageMeter } from "./components/LicenseUsageMeter";
import { MembersTable } from "./components/MembersTable";
import { SegmentedControl, type TeamTab } from "./components/SegmentedControl";
import { InviteMemberModal } from "./modals/InviteMemberModal";
import type { Invitation, TeamMember } from "./types";

const PAGE_SIZE = 50;

export function TeamPage() {
  const { t } = useTranslation();
  const currentUser = useCurrentUser();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab: TeamTab = searchParams.get("tab") === "pending" ? "pending" : "members";
  const track = useTrack();

  const [page, setPage] = useState(1);
  const accountId = currentUser?.accountId;

  const { account, refresh: refreshAccount } = useAccount(accountId);
  const members = useTeamMembers(page, PAGE_SIZE);

  const setTab = useCallback(
    (value: TeamTab) => {
      const next = new URLSearchParams(searchParams);
      if (value === "members") next.delete("tab");
      else next.set("tab", value);
      setSearchParams(next, { replace: true });
      // Mirrors Team.vue `switchTabMyTeam` — fire after the URL update
      // so the event lands with the new tab as context.
      const action =
        value === "members"
          ? "Team Management > Open Members tab"
          : "Team Management > Open Invitations tab";
      track(action, { eventCategory: MY_TEAM, eventLabel: account?.name ?? "" });
    },
    [searchParams, setSearchParams, track, account?.name],
  );
  const invitations = useInvitations();

  const updateRole = useUpdateMemberRole();
  const updateLicense = useUpdateMemberLicense();
  const removeMember = useRemoveMember();
  const leaveTeam = useLeaveTeam();
  const withdrawInvitation = useWithdrawInvitation();
  const customerPortal = useCreateCustomerPortal();
  const goToBulkLicensePage = useGoToBulkLicensePage();
  const requestAdditionalLicense = useHostRequestAdditionalLicense();
  const confirm = useHostConfirm();
  const toast = useHostToast();

  const [inviteOpen, setInviteOpen] = useState(false);

  // AHA-41755 bulk-action selection. Kept here (TeamPage), not in
  // MembersTable, so cohort counts can include members not on the current
  // page (selection persists across pagination) and the host bridge has a
  // single emitter. Selection is cleared on page change — applying a bulk
  // action only ever fans out over rows we currently have in `members.members`
  // so cross-page selections couldn't be reliably executed anyway.
  const [selectedMemberIds, setSelectedMemberIds] = useState<string[]>([]);
  // Lock against concurrent bulk dispatches. Two rapid clicks from the
  // host (e.g. the user hammers Assign while the loop is still in flight)
  // would otherwise interleave grant/revoke calls and double-toast.
  const [bulkRunning, setBulkRunning] = useState(false);

  // AHA-43040: SCIM gating mirrors the presenter's logic
  // (`Team.vue` + `featureToggles.module.js`):
  //   scimEnabled                 — host-supplied: user.isScimEnabled
  //                                 && featureToggles/canUseSCIM
  //   canUseScimManualManagement  — Unleash override
  //                                 (`AHA-42627-scim-manual-management`),
  //                                 lets ATB-style teams keep manual
  //                                 invite / role / licence / remove
  //                                 controls despite scimEnabled.
  // Reads inline (not via state) — by the time `currentUser` resolves,
  // the zoid xprops blob is hydrated, so re-reads land the real values.
  const scimEnabled = Boolean(currentUser?.accountId) && Boolean(getHost()?.scimEnabled);
  const canUseScimManualManagement = isHostFeatureEnabled(SCIM_MANUAL_MANAGEMENT_FLAG);
  // Account-derived flags computed on the backend (mirrors the
  // presenter's Vuex getters in `vuex/getters/currentTeam.js`).
  const isPaidTeamPlan = Boolean(account?.isPaidTeamPlan);
  const isUsedLicenseGreaterThanQuota = Boolean(account?.isUsedLicenseGreaterThanQuota);
  // AHA-43204: seats currently free. Falls back to `0` when the account row
  // isn't loaded yet so the Grant menu item stays disabled until we have real
  // numbers — better than briefly enabling and then disabling.
  // AHA-46193: the same count is reported to the host (`availableLicenses`) so
  // it can disable the bulk "Assign" bar button when there are 0 free seats —
  // the host previously only had `assignableCount` (how many selected rows
  // *would* be granted) and so left Assign enabled even with no seats left.
  const availableLicenses = Math.max(
    0,
    Number(account?.licenseQuantity ?? 0) -
      Number(account?.numberOfUsedLicenses ?? 0),
  );
  const canGrantMore = availableLicenses > 0;
  // Mirrors the presenter's `currentUserIsTeamOwnerOrAdmin` getter
  // (vuex/getters/currentUser.js): the viewer's role lives on
  // currentUser (zoid prop), not on the account record.
  const currentUserIsTeamOwnerOrAdmin = useMemo(() => {
    if (!currentUser?.accountId) return false;
    const role = currentUser?.accountRole;
    return role === "owner" || role === "admin";
  }, [currentUser?.accountId, currentUser?.accountRole]);
  const isSelfCreateTeam = account?.createdFrom === "presenter";
  // Mirrors `isTeamCreatedByCustomerSuccess` getter — the backend
  // already computes it (createdFrom == null || createdFrom === "admin").
  // CS-managed teams must NOT be sent to the Stripe self-serve checkout
  // (their billing arrangement lives outside Stripe); instead the host
  // opens a request-license form that posts to Slack. See AHA-42841.
  const isTeamCreatedByCustomerSuccess = Boolean(account?.isTeamCreatedByCustomerSuccess);

  const onUpdateMemberRole = useCallback(
    async (member: TeamMember, role: string) => {
      if (!accountId) return;
      // Mirrors MemberRoleSelectionV2.updateMemberRole tracking.
      track("Select role", {
        eventAction: "Team management > Member list > Update member's role",
        eventCategory: "Team Members",
        eventLabel: member.email,
        plan: currentUser?.planType,
        role,
      });
      const ok = await updateRole.update(accountId, String(member.id), role);
      // Patch in place instead of refetching the whole list — preserves
      // scroll position and the per-row loading affordance.
      if (ok) {
        members.patchMember(member.id, { accountRole: role });
        // Some plans only count specific roles toward usedLicenses; the
        // backend is the source of truth, so refresh to be safe.
        refreshAccount();
      }
    },
    [accountId, updateRole, members, refreshAccount, track, currentUser?.planType],
  );

  const onUpdateMemberLicense = useCallback(
    async (member: TeamMember, licensed: boolean) => {
      if (!accountId) return;
      track("Select license", {
        eventAction: "Team management > Member list > Update member's license",
        eventCategory: "Team Members",
        eventLabel: member.email,
        plan: currentUser?.planType,
        licensed,
      });
      const { ok, license } = await updateLicense.update(String(member.id), licensed);
      // Patch the row in place (like role) so the cell flips without a full
      // refetch; refresh the account so the usage meter reflects the change.
      if (ok) {
        members.patchMember(member.id, { license });
        refreshAccount();
      }
    },
    [accountId, updateLicense, members, refreshAccount, track, currentUser?.planType],
  );

  const onRemove = useCallback(
    async (member: TeamMember) => {
      // Mirrors Team.vue `openConfirmRemoveMember`.
      track("Team Management > Remove from team", {
        eventCategory: MY_TEAM,
        eventLabel: account?.name ?? "",
      });
      const ok = await confirm({
        title: t("team.confirm.removeMember.title"),
        content: t("team.confirm.removeMember.content"),
        okText: t("team.action.removeFromTeam"),
        cancelText: t("team.action.cancel"),
        variant: "danger",
      });
      if (!ok) return;
      const removed = await removeMember.remove(member.id);
      if (removed) {
        members.refresh();
        // Removing a member frees a license — refresh the meter.
        refreshAccount();
      }
    },
    [confirm, removeMember, members, refreshAccount, t, track, account?.name],
  );

  const onLeave = useCallback(async () => {
    // Mirrors Team.vue `openConfirmLeaveTeam`.
    track("Team Management > Leave team", {
      eventCategory: MY_TEAM,
      eventLabel: account?.name ?? "",
    });
    const ok = await confirm({
      title: t("team.confirm.leaveTeam.title"),
      content: t("team.confirm.leaveTeam.content"),
      okText: t("team.action.leaveTeam"),
      cancelText: t("team.action.cancel"),
      variant: "danger",
    });
    if (!ok) return;
    await leaveTeam.leave();
  }, [confirm, leaveTeam, t, track, account?.name]);

  // AHA-41755 bulk-action — derived state + bridge wiring.
  //
  // Eligibility: same gates as the per-row dropdown — owner/admin, paid plan,
  // not SCIM-locked. When the viewer can't act on licences at all there's no
  // point showing checkboxes.
  const canManageManually = !scimEnabled || canUseScimManualManagement;
  const bulkSelectionEligible =
    currentUserIsTeamOwnerOrAdmin && isPaidTeamPlan && canManageManually;

  // Clear selection when the visible page changes; cross-page bulk apply
  // is unreliable (we'd be acting on rows we don't have locally), so we
  // scope every bulk action to the page in view.
  useEffect(() => {
    setSelectedMemberIds([]);
  }, [page]);

  // Cohort counts — restricted to members CURRENTLY in `members.members` so
  // the numbers always match what the user sees. AntD's row-selection state
  // can outlive the data (e.g. a refetch dropped a row); the intersection
  // here keeps the bar's count honest.
  const { assignableCount, revocableCount, visibleSelectedIds } = useMemo(() => {
    const selectedSet = new Set(selectedMemberIds);
    const teamPlan = account?.plan ?? null;
    const viewerRole = currentUser?.accountRole;
    const visible: string[] = [];
    let assignable = 0;
    let revocable = 0;
    for (const m of members.members) {
      const id = String(m.id);
      if (!selectedSet.has(id)) continue;
      visible.push(id);
      // AHA-43057: an admin viewer can't act on owner/admin rows, so they
      // never count toward the assignable/revocable cohorts (defence in depth
      // — such rows also can't be selected in the first place).
      if (!isRoleActionableByViewer(m, viewerRole)) continue;
      if (isAssignableMember(m, teamPlan)) assignable++;
      else if (isRevocableMember(m, teamPlan)) revocable++;
    }
    return {
      assignableCount: assignable,
      revocableCount: revocable,
      visibleSelectedIds: visible,
    };
  }, [members.members, selectedMemberIds, account?.plan, currentUser?.accountRole]);

  useHostReportSelection({
    selectedIds: visibleSelectedIds,
    assignableCount,
    revocableCount,
    availableLicenses,
  });

  // Host dispatches an action via zoid `updateProps` after its confirm modal
  // accepts. One bulk API call per dispatch (not N round-trips) — the
  // backend partitions the cohort server-side and returns per-userId
  // outcomes for grant (granted vs skipped:already_licensed/cap_exceeded);
  // revoke is atomic by definition and returns the rows it actually flipped.
  const runBulkAction = useCallback(
    async (action: "assign" | "revoke") => {
      if (!accountId) return;
      if (bulkRunning) return; // concurrent-dispatch guard
      const teamPlan = account?.plan ?? null;
      const viewerRole = currentUser?.accountRole;
      const targets = members.members.filter((m) => {
        if (!selectedMemberIds.includes(String(m.id))) return false;
        // AHA-43057: never dispatch a bulk grant/revoke for a row the viewer's
        // role can't act on (owner/admin rows for an admin viewer). UI-only
        // restriction — the backend does not gate on the target's role.
        if (!isRoleActionableByViewer(m, viewerRole)) return false;
        return action === "assign"
          ? isAssignableMember(m, teamPlan)
          : isRevocableMember(m, teamPlan);
      });
      if (targets.length === 0) return;

      track("Team management > Bulk license action", {
        eventCategory: MY_TEAM,
        eventLabel: action,
        plan: currentUser?.planType,
        count: targets.length,
      });

      setBulkRunning(true);
      let ok = 0;
      let failed = 0;
      try {
        if (action === "assign") {
          const result = await grantLicenseBulk(targets.map((m) => m.id));
          // Patch each row that the server actually granted. Skipped rows
          // (already_licensed / cap_exceeded) don't get patched — their
          // license state didn't change.
          for (const license of result.granted) {
            // Bulk-grant always returns `userId` on each license row (it's
            // how the server tells us which member it belongs to). Guard
            // anyway in case a future server change omits it.
            if (license.userId != null) {
              members.patchMember(license.userId, { license });
            }
            ok++;
          }
          failed = result.skipped.length;
        } else {
          const result = await revokeLicenseBulk(targets.map((m) => m.id));
          for (const userId of result.revokedUserIds) {
            members.patchMember(userId, { license: null });
          }
          ok = result.revoked;
          // Anything we asked to revoke that didn't come back was a no-op
          // (already not licensed by the time the server ran) — count as
          // "failed" for the toast summary so the operator sees that not
          // all of their selection was acted on.
          failed = targets.length - result.revoked;
        }
      } catch {
        // Whole batch failed (network / 5xx). Treat everything as failed.
        failed = targets.length;
      } finally {
        setBulkRunning(false);
        // Tell the host the in-flight action is done so it can clear its
        // spinner / re-enable the bar buttons. Fires for both success and
        // failure paths (the host doesn't need to distinguish — the embed
        // already toasts).
        getHost()?.onBulkActionComplete?.();
      }
      if (ok > 0) refreshAccount();
      // Clear only the rows we acted on — leftover cohort (different
      // action type) stays selected for a follow-up dispatch.
      setSelectedMemberIds((prev) =>
        prev.filter((id) => !targets.some((m) => String(m.id) === id)),
      );

      const actionLabel = t(
        action === "assign" ? "team.action.assign" : "team.action.revoke",
      );
      if (failed === 0) {
        toast.success(
          t("team.toast.bulkLicenseSuccess", {
            count: ok,
            action: actionLabel,
          }),
        );
      } else if (ok === 0) {
        toast.error(
          t("team.toast.bulkLicenseAllFailed", {
            count: failed,
            action: actionLabel,
          }),
        );
      } else {
        toast.error(
          t("team.toast.bulkLicensePartial", {
            ok,
            failed,
            action: actionLabel,
          }),
        );
      }
    },
    [
      accountId,
      bulkRunning,
      account?.plan,
      members,
      selectedMemberIds,
      refreshAccount,
      currentUser?.planType,
      currentUser?.accountRole,
      toast,
      t,
      track,
    ],
  );

  useHostBulkActionListener((req) => {
    if (req.action === "clear") {
      setSelectedMemberIds([]);
      return;
    }
    void runBulkAction(req.action);
  });

  const onWithdraw = useCallback(
    async (invitation: Invitation) => {
      // Mirrors Team.vue `showWithdrawInvitationModal`.
      track("Team management > Invite member > Click Withdraw", {
        eventCategory: MY_TEAM,
        eventLabel: invitation.invitedEmail,
      });
      const ok = await confirm({
        title: t("team.confirm.withdrawInvitation.title"),
        content: t("team.confirm.withdrawInvitation.content"),
        okText: t("team.action.withdrawInvitation"),
        cancelText: t("team.action.cancel"),
        variant: "danger",
      });
      if (!ok) return;
      const success = await withdrawInvitation.withdraw(invitation.id, invitation.invitedEmail);
      if (success) {
        invitations.refresh();
        // Some plans count pending invitations toward usedLicenses;
        // refresh the meter for safety.
        refreshAccount();
      }
    },
    [confirm, withdrawInvitation, invitations, refreshAccount, t, track],
  );

  const onCopyLink = useCallback(
    async (invitation: Invitation) => {
      if (!invitation.link) return;
      try {
        await navigator.clipboard.writeText(invitation.link);
        toast.success(t("team.toast.linkCopied"));
      } catch {
        toast.error(t("team.toast.genericError"));
      }
    },
    [t, toast],
  );

  const onUpgrade = useCallback(() => {
    // Mirrors Team.vue `handleUpgradeTeam`. Order matters: the CS-team
    // branch must run BEFORE the paid-plan branch — a CS-managed team
    // is on a paid plan but its billing is handled outside Stripe, so
    // routing those owners to the customer portal would let them
    // self-serve charges that bypass the agreed arrangement
    // (AHA-42841).
    const buttonName = isPaidTeamPlan ? "Buy licenses" : "Upgrade team";
    track("Team management > Upgrade", {
      eventCategory: MY_TEAM,
      eventLabel: buttonName,
      eventAction: "Team management > Upgrade",
    });
    if (isTeamCreatedByCustomerSuccess) {
      if (requestAdditionalLicense.canRequest) {
        requestAdditionalLicense.request();
      } else {
        // Older host build or standalone dev — the request form lives
        // on the host so we can't render it inline. Surface an actionable
        // message instead of silently doing nothing or (worse) falling
        // through to the Stripe checkout.
        toast.info(t("team.toast.contactSupportForLicenses"));
      }
      return;
    }
    if (isPaidTeamPlan) customerPortal.open({ isUpdatingPlan: true });
    else goToBulkLicensePage();
  }, [
    isPaidTeamPlan,
    isTeamCreatedByCustomerSuccess,
    customerPortal,
    goToBulkLicensePage,
    requestAdditionalLicense,
    toast,
    t,
    track,
  ]);

  // Export the rows currently in view to a CSV download. Mirrors the active
  // tab's visible columns (members vs. invitations) and is gated on the same
  // owner/admin flags the table uses, so the file matches the screen 1:1.
  const onExport = useCallback(() => {
    // Local calendar date (not UTC) so the filename matches the user's day.
    const date = dayjs().format("YYYY-MM-DD");
    track("Team management > Export CSV", {
      eventCategory: MY_TEAM,
      eventLabel: tab,
    });
    if (tab === "pending") {
      const table = buildInvitationsCsv(invitations.invitations, {
        isPaidTeamPlan,
        t,
      });
      downloadCsv(`team-invitations-${date}.csv`, toCsv(table));
      return;
    }
    const table = buildMembersCsv(members.members, {
      showDetailedColumns: currentUserIsTeamOwnerOrAdmin,
      isPaidTeamPlan,
      teamPlan: account?.plan ?? null,
      t,
    });
    downloadCsv(`team-members-${date}.csv`, toCsv(table));
  }, [
    tab,
    invitations.invitations,
    members.members,
    currentUserIsTeamOwnerOrAdmin,
    isPaidTeamPlan,
    account?.plan,
    track,
    t,
  ]);

  const showLicenseShortage =
    Boolean(account?.id) && currentUserIsTeamOwnerOrAdmin && isUsedLicenseGreaterThanQuota;

  const membersTabLabel = members.total > 0
    ? `${t("team.tab.members")} (${members.total})`
    : t("team.tab.members");
  // Count only invitations that are still actionable (i.e. not expired).
  // The table itself still renders every row regardless of status —
  // declined/expired rows stay visible so the owner can see history —
  // but the tab badge mirrors the presenter's behaviour of counting
  // only pending invitations. See AHA-42794.
  const pendingInvitationCount = invitations.invitations.filter(
    (i) => i.status !== "expired",
  ).length;
  const pendingTabLabel = `${t("team.tab.pending")} (${pendingInvitationCount})`;

  return (
    <div
      data-testid="team-management-content"
      // AHA-43204: 4px of inset padding around the page so focus rings on
      // edge controls (Members/Pending tabs on the left, Invite button on
      // the right) have room to render without being clipped by the iframe
      // boundary. AntD's default :focus-visible style is an outer
      // box-shadow with ~2px spread; without padding the outermost px of
      // the ring lands outside the iframe content box and looks like a
      // cropped corner. 4px gives a 2px safety margin on top of the
      // shadow.
      style={{ display: "flex", flexDirection: "column", gap: 24, padding: 4 }}
    >
      {showLicenseShortage && (
        <LicenseShortageAlert onBuyLicenses={onUpgrade} buying={customerPortal.pending} />
      )}

      <div
        data-testid="team-management-toolbar"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 16,
          flexWrap: "wrap",
        }}
      >
        <SegmentedControl
          value={tab}
          onChange={setTab}
          membersLabel={membersTabLabel}
          pendingLabel={pendingTabLabel}
        />
        {/* Action group (license meter + invite + upgrade) is owner/admin
            only — matches `showActions` gate on TeamManagementV2Layout. */}
        {currentUserIsTeamOwnerOrAdmin && (
          <div
            data-testid="team-management-toolbar-actions"
            // AHA2-21: gap between the licence-meter block and the button
            // cluster = 23px, cloned from the reference's `gap-[23px]`
            // (TeamManagementV2Layout.vue @ acc3d51). Both children are 40px
            // tall and centre-aligned.
            style={{ display: "flex", alignItems: "center", gap: 23 }}
          >
            <LicenseUsageMeter
              account={account}
              currentUserIsTeamOwnerOrAdmin={currentUserIsTeamOwnerOrAdmin}
              isPaidTeamPlan={isPaidTeamPlan}
              isUsedLicenseGreaterThanQuota={isUsedLicenseGreaterThanQuota}
            />
            <ActionButtons
              currentUserIsTeamOwner={currentUser?.accountRole === "owner"}
              isPaidTeamPlan={isPaidTeamPlan}
              isAccountLoaded={account != null}
              isCreatingCustomerPortal={customerPortal.pending}
              scimEnabled={scimEnabled}
              canUseScimManualManagement={canUseScimManualManagement}
              onUpgrade={onUpgrade}
              onInvite={() => setInviteOpen(true)}
            />
          </div>
        )}
      </div>

      {tab === "members" && (
        <>
          <MembersTable
            members={members.members}
            loading={members.loading}
            currentUserId={currentUser?.id}
            currentUserAccountRole={currentUser?.accountRole}
            currentUserIsTeamOwnerOrAdmin={currentUserIsTeamOwnerOrAdmin}
            isSelfCreateTeam={Boolean(isSelfCreateTeam)}
            showDetailedColumns={currentUserIsTeamOwnerOrAdmin}
            showActionColumn
            scimEnabled={scimEnabled}
            canUseScimManualManagement={canUseScimManualManagement}
            teamPlan={account?.plan}
            isPaidTeamPlan={isPaidTeamPlan}
            canGrantMore={canGrantMore}
            updatingMemberId={updateRole.pendingMemberId}
            updatingLicenseMemberId={updateLicense.pendingMemberId}
            onUpdateMemberRole={onUpdateMemberRole}
            onUpdateMemberLicense={onUpdateMemberLicense}
            onRemove={onRemove}
            onLeave={onLeave}
            selectedMemberIds={bulkSelectionEligible ? selectedMemberIds : undefined}
            onSelectionChange={bulkSelectionEligible ? setSelectedMemberIds : undefined}
            header={
              currentUserIsTeamOwnerOrAdmin ? (
                <ExportButton onExport={onExport} disabled={members.loading} />
              ) : undefined
            }
          />
          {members.total > PAGE_SIZE && (
            <div style={{ display: "flex", justifyContent: "center" }}>
              <Pagination
                current={page}
                total={members.total}
                pageSize={PAGE_SIZE}
                onChange={setPage}
                showSizeChanger={false}
              />
            </div>
          )}
        </>
      )}

      {tab === "pending" && (
        <InvitationsTable
          invitations={invitations.invitations}
          loading={invitations.loading}
          error={Boolean(invitations.error)}
          isPaidTeamPlan={isPaidTeamPlan}
          onCopyLink={onCopyLink}
          onWithdraw={onWithdraw}
          header={
            currentUserIsTeamOwnerOrAdmin ? (
              <ExportButton onExport={onExport} disabled={invitations.loading} />
            ) : undefined
          }
        />
      )}

      <InviteMemberModal
        open={inviteOpen}
        account={account}
        teamSize={members.total + pendingInvitationCount}
        currentUserIsTeamOwner={currentUser?.accountRole === "owner"}
        isPaidTeamPlan={isPaidTeamPlan}
        onClose={() => setInviteOpen(false)}
        onInvited={() => {
          invitations.refresh();
          // New invitations bump usedLicenses on most plans — refresh
          // the meter so the new total shows without a page reload.
          refreshAccount();
          setTab("pending");
        }}
        onBuyLicenses={onUpgrade}
      />
    </div>
  );
}
